import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Card,
  CardContent,
  Link,
  Tab,
  Tabs,
  Alert,
  Snackbar,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  useGetPendingApplicationsQuery,
  useApproveApplicationMutation,
  useRejectApplicationMutation,
  JoiningApplication,
  useGetInvitationRequestsQuery,
  useCreateInvitationCodeMutation,
  InvitationRequest,
  useGetInvitationLogsQuery,
} from "../../../entities/admin-joining";
import {
  useGetCommunityUsersQuery,
  CommunityUser,
} from "../../../entities/admin-communities";
import { format } from "date-fns";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CakeIcon from "@mui/icons-material/Cake";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import { LoadingIndicator } from "../../../shared/ui/loading-indicator";
import { debounce } from "../../../shared/lib/debounce";
import {
  ResidentCaliber,
  getResidentDisplayName,
} from "../../../entities/admin-events";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`application-tabpanel-${index}`}
      aria-labelledby={`application-tab-${index}`}
      {...other}
      sx={{ mt: 2 }}
    >
      {value === index && <Box>{children}</Box>}
    </Box>
  );
}

export const JoiningApplicationsManagement = () => {
  const [selectedApplication, setSelectedApplication] =
    useState<JoiningApplication | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [selectedCaliber, setSelectedCaliber] = useState<ResidentCaliber | "">(
    ""
  );
  const [mainTabValue, setMainTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [creatingCodeForId, setCreatingCodeForId] = useState<number | null>(
    null
  );
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<CommunityUser | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>("");

  // Queries and mutations
  const { data: applicationsData, isLoading: isLoadingApplications } =
    useGetPendingApplicationsQuery();
  const {
    data: invitationRequestsData,
    isLoading: isLoadingInvitationRequests,
  } = useGetInvitationRequestsQuery();
  const [approveApplication, { isLoading: isApproving }] =
    useApproveApplicationMutation();
  const [rejectApplication, { isLoading: isRejecting }] =
    useRejectApplicationMutation();
  const [createInvitationCode] = useCreateInvitationCodeMutation();
  const { data: invitationLogsData, isLoading: isLoadingLogs } =
    useGetInvitationLogsQuery(selectedUserId, {
      skip: !selectedUserId,
    });
  const searchParams = useMemo(
    () => ({
      limit: 50,
      ...(debouncedSearchQuery && { search: debouncedSearchQuery }),
    }),
    [debouncedSearchQuery]
  );

  const { data: communityUsersData, isLoading: isLoadingUsers } =
    useGetCommunityUsersQuery(searchParams);

  // Debounced search effect
  const debouncedSetSearch = useCallback((query: string) => {
    const debouncedFn = debounce((q: string) => {
      console.log("Setting debounced search query:", q);
      setDebouncedSearchQuery(q);
    }, 500);
    debouncedFn(query);
  }, []);

  useEffect(() => {
    debouncedSetSearch(searchQuery);
  }, [searchQuery, debouncedSetSearch]);

  // Handlers
  const handleSelectApplication = (application: JoiningApplication) => {
    setSelectedApplication(application);
    setTabValue(0);
  };

  const handleOpenRejectDialog = () => {
    setRejectReason("");
    setOpenRejectDialog(true);
  };

  const handleCloseRejectDialog = () => {
    setOpenRejectDialog(false);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleMainTabChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ) => {
    setMainTabValue(newValue);
    setSelectedApplication(null);
  };

  const handleOpenApproveDialog = () => {
    setSelectedCaliber("");
    setOpenApproveDialog(true);
  };

  const handleCloseApproveDialog = () => {
    setOpenApproveDialog(false);
  };

  const handleConfirmApprove = async () => {
    if (!selectedApplication || !selectedCaliber) return;

    try {
      await approveApplication({
        id: selectedApplication.id,
        caliber: selectedCaliber,
      }).unwrap();
      setSelectedApplication(null);
      setOpenApproveDialog(false);
      setSnackbar({
        open: true,
        message: "Заявка успешно одобрена",
        severity: "success",
      });
    } catch (error) {
      console.error("Error approving application:", error);
      setSnackbar({
        open: true,
        message: "Ошибка при одобрении заявки",
        severity: "error",
      });
    }
  };

  const handleRejectApplication = async () => {
    if (!selectedApplication) return;

    try {
      await rejectApplication({
        id: selectedApplication.id,
        body: {
          rejectedMessage: rejectReason.trim(),
        },
      }).unwrap();
      setSelectedApplication(null);
      handleCloseRejectDialog();
      setSnackbar({
        open: true,
        message: "Заявка успешно отклонена",
        severity: "success",
      });
    } catch (error) {
      console.error("Error rejecting application:", error);
      setSnackbar({
        open: true,
        message: "Ошибка при отклонении заявки",
        severity: "error",
      });
    }
  };

  const handleCreateInvitationCode = async (request: InvitationRequest) => {
    setCreatingCodeForId(request.id);
    try {
      const result = await createInvitationCode(request.id).unwrap();
      setSnackbar({
        open: true,
        message: `Код приглашения создан: ${result.code}`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error creating invitation code:", error);
      setSnackbar({
        open: true,
        message: "Ошибка при создании кода приглашения",
        severity: "error",
      });
    } finally {
      setCreatingCodeForId(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setSnackbar({
        open: true,
        message: "Код скопирован в буфер обмена",
        severity: "success",
      });
    } catch (error) {
      console.error("Error copying code:", error);
      setSnackbar({
        open: true,
        message: "Ошибка при копировании кода",
        severity: "error",
      });
    }
  };

  const handleSelectUser = (user: CommunityUser) => {
    setSelectedUser(user);
    setSelectedUserId(user.id);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    console.log("Search query changed:", query);
    setSearchQuery(query);
  };

  // Render users list
  const renderUsersList = () => {
    return (
      <Box mt={2}>
        <TextField
          fullWidth
          label="Поиск пользователей"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Введите имя или фамилию"
          sx={{ mb: 2 }}
          size="small"
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
            ),
          }}
        />

        {isLoadingUsers ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : !communityUsersData?.users?.length ? (
          <Typography color="text.secondary" align="center" py={3}>
            {searchQuery ? "Пользователи не найдены" : "Нет пользователей"}
          </Typography>
        ) : (
          <List sx={{ maxHeight: 350, overflow: "auto" }}>
            {communityUsersData.users.map((user) => (
              <ListItem
                key={user.id}
                sx={{
                  borderRadius: 1,
                  backgroundColor:
                    selectedUser?.id === user.id ? "#515151" : "inherit",
                  "&:hover": {
                    cursor: "pointer",
                    backgroundColor: "#414141",
                  },
                  mb: 1,
                  border: 1,
                  borderColor: "divider",
                }}
                onClick={() => handleSelectUser(user)}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  {user.avatarUrl && (
                    <Box
                      component="img"
                      src={user.avatarUrl}
                      alt={`${user.firstName} ${user.lastName}`}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        mr: 2,
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.phoneNumber}
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mt: 0.5 }}
                    >
                      <Chip
                        label={user.role}
                        size="small"
                        color={user.role === "resident" ? "success" : "default"}
                      />
                      {user.blocked && (
                        <Chip
                          label="Заблокирован"
                          size="small"
                          color="error"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    );
  };

  // Render invitation logs
  const renderInvitationLogs = () => {
    return (
      <Box>
        {selectedUser && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              {selectedUser.avatarUrl && (
                <Box
                  component="img"
                  src={selectedUser.avatarUrl}
                  alt={`${selectedUser.firstName} ${selectedUser.lastName}`}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    mr: 2,
                  }}
                />
              )}
              <Typography variant="h6">
                История приглашений: {selectedUser.firstName}{" "}
                {selectedUser.lastName}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Телефон: {selectedUser.phoneNumber}
            </Typography>
          </Box>
        )}

        {isLoadingLogs && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {invitationLogsData && invitationLogsData.items.length > 0 && (
          <List>
            {invitationLogsData.items.map((log) => (
              <ListItem
                key={log.id}
                sx={{
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  mb: 1,
                  flexDirection: "column",
                  alignItems: "stretch",
                }}
              >
                <Box sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <PersonIcon sx={{ mr: 1, color: "text.secondary" }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                          {log.relatedUser.firstName} {log.relatedUser.lastName}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Тип: {log.typeName}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        variant="body2"
                        color="success.main"
                        fontWeight={600}
                      >
                        Код: {log.code}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(log.code)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Создан:{" "}
                      {format(new Date(log.createdAt), "dd.MM.yyyy HH:mm")}
                    </Typography>
                    <Chip
                      label={log.type}
                      size="small"
                      color={log.type === "requested" ? "info" : "default"}
                    />
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        )}

        {invitationLogsData &&
          invitationLogsData.items.length === 0 &&
          selectedUser && (
            <Typography color="text.secondary" align="center" py={3}>
              Нет истории по данному пользователю
            </Typography>
          )}

        {!selectedUser && (
          <Typography color="text.secondary" align="center" py={3}>
            Выберите пользователя из списка для просмотра истории приглашений
          </Typography>
        )}
      </Box>
    );
  };

  // Render invitation requests
  const renderInvitationRequests = () => {
    if (isLoadingInvitationRequests) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!invitationRequestsData?.items?.length) {
      return (
        <Typography color="text.secondary" align="center" py={3}>
          Нет запросов на код приглашения
        </Typography>
      );
    }

    return (
      <List>
        {invitationRequestsData.items.map((request) => (
          <ListItem
            key={request.id}
            sx={{
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              mb: 1,
              flexDirection: "column",
              alignItems: "stretch",
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 1,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {request.firstName} {request.lastName}{" "}
                    {request.patronymic || ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {request.phoneNumber}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CodeIcon />}
                  onClick={() => handleCreateInvitationCode(request)}
                  disabled={creatingCodeForId === request.id}
                  size="medium"
                >
                  Сформировать код
                  {creatingCodeForId === request.id && <LoadingIndicator />}
                </Button>
              </Box>

              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Создан:{" "}
                  {format(new Date(request.createdAt), "dd.MM.yyyy HH:mm")}
                </Typography>
                {request.expirationAt && (
                  <Typography variant="body2" color="text.secondary">
                    Истекает:{" "}
                    {format(new Date(request.expirationAt), "dd.MM.yyyy HH:mm")}
                  </Typography>
                )}
                {request.code && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      variant="body2"
                      color="success.main"
                      fontWeight={600}
                    >
                      Код: {request.code}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyCode(request.code)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
          </ListItem>
        ))}
      </List>
    );
  };

  // Render application details
  const renderApplicationDetails = () => {
    if (!selectedApplication) return null;

    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5">
            {selectedApplication.firstName} {selectedApplication.lastName}{" "}
            {selectedApplication.patronymic || ""}
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="success"
              startIcon={<ThumbUpIcon />}
              onClick={handleOpenApproveDialog}
              disabled={isApproving}
              sx={{ mr: 1 }}
            >
              {isApproving ? <CircularProgress size={24} /> : "Одобрить"}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<ThumbDownIcon />}
              onClick={handleOpenRejectDialog}
              disabled={isRejecting}
            >
              Отклонить
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            Дата подачи:
          </Typography>
          <Typography variant="body2">
            {format(
              new Date(selectedApplication.createdAt),
              "dd.MM.yyyy HH:mm"
            )}
          </Typography>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="application tabs"
          >
            <Tab label="Основная информация" />
            <Tab label="Мотивация" />
            <Tab label="Навыки и хобби" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Контактные данные
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <EmailIcon sx={{ mr: 1, color: "text.secondary" }} />
                    <Typography>
                      {selectedApplication.email}{" "}
                      {selectedApplication.emailConfirmed && (
                        <Chip
                          label="Подтвержден"
                          color="success"
                          size="small"
                        />
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <PhoneIcon sx={{ mr: 1, color: "text.secondary" }} />
                    <Typography>{selectedApplication.phoneNumber}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Личные данные
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <CakeIcon sx={{ mr: 1, color: "text.secondary" }} />
                    <Typography>
                      {format(
                        new Date(selectedApplication.birthDate),
                        "dd.MM.yyyy"
                      )}{" "}
                      ({selectedApplication.gender})
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <LocationCityIcon sx={{ mr: 1, color: "text.secondary" }} />
                    <Typography>{selectedApplication.city.name}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Социальные сети
                  </Typography>
                  {selectedApplication.socials.length ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {selectedApplication.socials.map((social, index) => (
                        <Chip
                          key={index}
                          label={`${social.type}: ${social.url}`}
                          component={Link}
                          href={social.url}
                          target="_blank"
                          clickable
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      Социальные сети не указаны
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Мотивация и ожидания
              </Typography>
              <Typography variant="body1" paragraph>
                {selectedApplication.motivationAndExpectations || "Не указано"}
              </Typography>
              <Typography variant="h6" gutterBottom>
                Полезные навыки и знания
              </Typography>
              <Typography variant="body1">
                {selectedApplication.usefulSkillsAndKnowledge || "Не указано"}
              </Typography>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Хобби
                  </Typography>
                  {selectedApplication.hobbies.length ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {selectedApplication.hobbies.map((hobby) => (
                        <Chip key={hobby.id} label={hobby.name} />
                      ))}
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      Хобби не указаны
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Компетенции
                  </Typography>
                  {selectedApplication.competencies.length ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {selectedApplication.competencies.map((competency) => (
                        <Chip key={competency.id} label={competency.name} />
                      ))}
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      Компетенции не указаны
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight={600} fontSize={24} mb={2}>
        Заявки на вступление в клуб
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={mainTabValue}
          onChange={handleMainTabChange}
          aria-label="main tabs"
        >
          <Tab label="Заявки на рассмотрение" />
          <Tab label="Запросы на код приглашения" />
          <Tab label="История приглашений" />
        </Tabs>
      </Box>

      {mainTabValue === 0 ? (
        <Box sx={{ display: "flex", gap: 2 }}>
          <Paper sx={{ width: 350, p: 2 }}>
            <Typography gutterBottom fontWeight={600} fontSize={18}>
              Ожидающие рассмотрения
            </Typography>

            {isLoadingApplications ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : applicationsData?.applications?.length ? (
              <List>
                {applicationsData.applications.map((application) => (
                  <ListItem
                    key={application.id}
                    sx={{
                      borderRadius: 1,
                      backgroundColor:
                        selectedApplication?.id === application.id
                          ? "#515151"
                          : "inherit",
                      "&:hover": {
                        cursor: "pointer",
                        backgroundColor: "#414141",
                      },
                      mb: 1,
                    }}
                    onClick={() => handleSelectApplication(application)}
                  >
                    <ListItemText
                      primary={`${application.firstName} ${application.lastName}`}
                      secondary={format(
                        new Date(application.createdAt),
                        "dd.MM.yyyy"
                      )}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="text.secondary" align="center" py={3}>
                Нет заявок, ожидающих рассмотрения
              </Typography>
            )}
          </Paper>

          <Paper sx={{ flex: 1, p: 2 }}>
            {selectedApplication ? (
              renderApplicationDetails()
            ) : (
              <Typography color="text.secondary" align="center" py={3}>
                Выберите заявку для просмотра деталей
              </Typography>
            )}
          </Paper>
        </Box>
      ) : mainTabValue === 1 ? (
        <Paper sx={{ p: 2 }}>
          <Typography gutterBottom fontWeight={600} fontSize={18} mb={2}>
            Запросы на код приглашения
          </Typography>
          {renderInvitationRequests()}
        </Paper>
      ) : (
        <Box sx={{ display: "flex", gap: 2 }}>
          <Paper sx={{ width: 350, p: 2 }}>
            <Typography gutterBottom fontWeight={600} fontSize={18}>
              Пользователи сообщества
            </Typography>
            {renderUsersList()}
          </Paper>
          <Paper sx={{ flex: 1, p: 2 }}>
            <Typography gutterBottom fontWeight={600} fontSize={18} mb={2}>
              История приглашений
            </Typography>
            {renderInvitationLogs()}
          </Paper>
        </Box>
      )}

      {/* Reject Dialog */}
      <Dialog
        open={openRejectDialog}
        onClose={handleCloseRejectDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Отклонить заявку</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Пожалуйста, укажите причину отклонения заявки:
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Причина отклонения"
            fullWidth
            multiline
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectDialog}>Отмена</Button>
          <Button
            onClick={handleRejectApplication}
            color="error"
            disabled={isRejecting || !rejectReason.trim()}
          >
            {isRejecting ? <CircularProgress size={24} /> : "Отклонить"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog
        open={openApproveDialog}
        onClose={handleCloseApproveDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle textAlign="center">Одобрить заявку</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Выберите категорию резидента для пользователя:
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="caliber-select-label">
              Категория резидента
            </InputLabel>
            <Select
              labelId="caliber-select-label"
              label="Категория резидента"
              value={selectedCaliber}
              onChange={(e) =>
                setSelectedCaliber(e.target.value as ResidentCaliber)
              }
            >
              <MenuItem value={ResidentCaliber.VOSHOD}>
                {getResidentDisplayName(ResidentCaliber.VOSHOD)}
              </MenuItem>
              <MenuItem value={ResidentCaliber.SIYAY}>
                {getResidentDisplayName(ResidentCaliber.SIYAY)}
              </MenuItem>
              <MenuItem value={ResidentCaliber.SOVER}>
                {getResidentDisplayName(ResidentCaliber.SOVER)}
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseApproveDialog}>Отмена</Button>
          <Button
            onClick={handleConfirmApprove}
            color="success"
            disabled={isApproving || !selectedCaliber}
          >
            {isApproving ? <CircularProgress size={24} /> : "Одобрить"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
